import React, { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import Input from '../forms/Input';
import {
  useLearningAreas,
  useLearningOutcomes,
} from '../../hooks/api/useCurriculum';
import type { LearningOutcome } from '../../types/api';

export interface LearningOutcomePickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired when the user selects an outcome. The picker closes itself. */
  onSelect: (outcome: LearningOutcome) => void;
  /** Outcome IDs already tagged on the parent observation -- highlighted
   * differently so the user can see what's already chosen. Optional;
   * single-tag forms can omit this. */
  selectedIds?: ReadonlySet<string>;
}

/**
 * CAPS Learning Outcome picker.
 *
 * Single-select for v1: clicking an outcome calls onSelect and closes the
 * modal. The schema-level multi-tag work is a separate piece — this is
 * scoped to land the immediate UX gap (no way to manually tag outcomes).
 *
 * Outcomes are grouped by Learning Area, with a search box that filters
 * by code, description, or area name. Filtering is local on whatever the
 * outcomes endpoint returned (usually a few hundred at most).
 */
const LearningOutcomePicker: React.FC<LearningOutcomePickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedIds,
}) => {
  const [search, setSearch] = useState('');

  const outcomesQuery = useLearningOutcomes();
  const areasQuery = useLearningAreas();

  const filtered = useMemo(() => {
    const outcomes = outcomesQuery.data ?? [];
    const areas = areasQuery.data ?? [];
    const areaById = new Map(areas.map((a) => [a.id, a]));

    const haystack = search.trim().toLowerCase();
    const visible = haystack
      ? outcomes.filter((o) => {
          const area = areaById.get(o.learning_area_id);
          const fields = [
            o.code,
            o.description,
            o.short_description ?? '',
            area?.name ?? '',
            area?.code ?? '',
          ];
          return fields.some((f) => f.toLowerCase().includes(haystack));
        })
      : outcomes;

    // Group by area for display. Empty areas are dropped from the list.
    const groups = new Map<string, { area: string; outcomes: LearningOutcome[] }>();
    for (const o of visible) {
      const area = areaById.get(o.learning_area_id);
      const key = area?.id ?? 'unknown';
      if (!groups.has(key)) {
        groups.set(key, {
          area: area?.name ?? 'Unassigned',
          outcomes: [],
        });
      }
      groups.get(key)!.outcomes.push(o);
    }
    return Array.from(groups.values()).sort((a, b) => a.area.localeCompare(b.area));
  }, [outcomesQuery.data, areasQuery.data, search]);

  const totalVisible = filtered.reduce((n, g) => n + g.outcomes.length, 0);
  const isLoading = outcomesQuery.isLoading || areasQuery.isLoading;
  const isError = outcomesQuery.isError || areasQuery.isError;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tag a CAPS Learning Outcome"
      size="lg"
    >
      <div className="space-y-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by code, description, or area…"
          icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
          data-testid="outcome-picker-search"
        />

        {isLoading ? (
          <div
            className="p-4 text-center text-sm text-gray-500 dark:text-gray-400"
            data-testid="outcome-picker-loading"
          >
            Loading curriculum…
          </div>
        ) : isError ? (
          <div className="p-4 text-center text-sm text-rose-600 dark:text-rose-400">
            Couldn't load the curriculum. Please try again.
          </div>
        ) : totalVisible === 0 ? (
          <div
            className="p-4 text-center text-sm text-gray-500 dark:text-gray-400"
            data-testid="outcome-picker-empty"
          >
            {search ? 'No outcomes match your search.' : 'No CAPS outcomes are seeded for this organization yet.'}
          </div>
        ) : (
          <ul
            className="max-h-96 overflow-y-auto space-y-3 pr-1"
            data-testid="outcome-picker-list"
          >
            {filtered.map((group) => (
              <li key={group.area}>
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 px-1 py-1 sticky top-0 bg-white dark:bg-gray-800">
                  {group.area}
                </div>
                <ul className="space-y-1">
                  {group.outcomes.map((o) => {
                    const isSelected = selectedIds?.has(o.id);
                    return (
                      <li key={o.id}>
                        <button
                          type="button"
                          onClick={() => {
                            onSelect(o);
                            onClose();
                          }}
                          data-testid={`outcome-option-${o.code}`}
                          className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                            isSelected
                              ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">
                              {o.code}
                            </span>
                            {isSelected && (
                              <span className="text-[10px] uppercase font-semibold text-emerald-700 dark:text-emerald-300">
                                Tagged
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                            {o.short_description || o.description}
                          </p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default LearningOutcomePicker;
